import { Box, Modal, Paper, Typography, Button } from '@mui/material';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import AlertToast from 'packages/novous_web/src/common/components/Alert';

type ChooseCompanyModalProps = {
  open: boolean;
  onClose: () => void;
  onCompanySelect: (companyData: { name: string; crn: string; id: string }) => void;
  selectedCompany?: { name: string; crn: string; id: string } | null;
};

export const ChooseCompanyModal: React.FC<ChooseCompanyModalProps> = ({ 
  open, 
  onClose, 
  onCompanySelect,
  selectedCompany: initialSelectedCompany
}) => {
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  const {
    data: companyList,
    error: companyListError,
    loading: companyListLoading,
    fetchData: fetchCompanyListData,
  } = useAxios<any>(`${API_ENDPOINTS.users.COMPANY_LIST}`, 'GET');

  useEffect(() => {
    if (open) {
      fetchCompanyListData();
      // Set initial selected company if provided
      if (initialSelectedCompany) {
        setSelectedCompany(initialSelectedCompany);
      }
    }
  }, [open, initialSelectedCompany]);

  useEffect(() => {
    if (companyListError) {
      setToast({
        open: true,
        message: companyListError,
        type: 'error',
      });
    }
  }, [companyListError]);

  const handleCompanyChange = (value: string) => {
    const company = companyList?.data?.find((c: any) => c.name === value);
    setSelectedCompany(company || null);
  };

  const handleSubmit = () => {
    if (!selectedCompany) {
      setToast({
        open: true,
        message: 'Please select a company',
        type: 'error',
      });
      return;
    }

    onCompanySelect({
      name: selectedCompany.name,
      crn: selectedCompany.crn,
      id: selectedCompany.id,
    });
    
    // Reset state and close modal
    setSelectedCompany(null);
    onClose();
  };

  const handleCancel = () => {
    setSelectedCompany(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleCancel}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '40%',
          minWidth: '400px',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Paper
          className="p-4 flex flex-col gap-4 rounded-lg"
          sx={{
            padding: 0,
            boxShadow: 'none',
          }}
        >
          <div className="flex flex-col gap-1">
            <Typography variant="h6" color="primary">
              Choose Company
            </Typography>
            <Typography variant="overline" fontWeight={600} color="secondary">
              Select a company to filter users and manage user creation for the specific organization.
            </Typography>
          </div>

          <div className="flex flex-col gap-4">
            <CustomDropDownInput
              label="Company"
              options={
                companyList?.data?.map((company: any) => ({
                  value: company.name,
                  label: company.name,
                })) || []
              }
              value={selectedCompany?.name || ''}
              onChange={handleCompanyChange}
              placeholder="Select Company"
              showDropdownArrow={true}
              isMandatory
            />
          </div>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              marginTop: 2,
              justifyContent: 'space-between',
            }}
          >
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!selectedCompany || companyListLoading}
            >
              Submit
            </Button>
          </Box>
        </Paper>

        <AlertToast
          open={toast.open}
          message={toast.message}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          type={toast.type as any}
        />
      </Box>
    </Modal>
  );
};

export default ChooseCompanyModal;
